// Resolver SchemaSnapshot puller. Re-exports per-engine projectors from
// `schemaProjectors.ts` and builds a `pullSchemaSnapshot(database)` closure
// bound to a (connectionId, engine) pair. Tests inject a mock `deps`;
// production wires the real `ensureXxxSchema` imports from the per-engine
// cache modules in `connections/services/language-services/*SchemaCache.ts`.

import type { SqlEngine } from '../../sql-grammar/model';
import type { SchemaSnapshot } from '../types';
import type { PgSchemaSnapshot } from '@/features/connections/services/language-services/pgSchemaTypes';
import type { MysqlSchemaSnapshot } from '@/features/connections/services/language-services/mysqlSchemaTypes';
import type { OracleSchemaSnapshot } from '@/features/connections/services/language-services/oracleSchemaTypes';
import type { SqliteSchemaSnapshot } from '@/features/connections/services/language-services/sqliteSchemaTypes';
import type { MssqlTableSnapshot } from '@/features/connections/services/language-services/mssqlTableCache';
import type { JsonColumnDeclaration, LooseRelationship } from '@/types/electron';
import { hashText } from '@/features/workspace/query-editor/utils/queryFileUtils';

import {
  projectPgSchema,
  projectMysqlSchema,
  projectOracleSchema,
  projectSqliteSchema,
  projectMssqlTables,
} from './schemaProjectors';
import { buildLooseJsonHintIndex } from '../json/looseJsonHintIndex';

export {
  projectPgSchema,
  projectMysqlSchema,
  projectOracleSchema,
  projectSqliteSchema,
  projectMssqlTables,
};

export type PullSchemaSnapshotFn = (
  database: string | undefined,
) => Promise<SchemaSnapshot | undefined>;

export type PullSchemaDeps = {
  readonly ensurePgSchema: (opts: { connectionId: string; database: string }) => Promise<PgSchemaSnapshot>;
  readonly ensureMysqlSchema: (opts: { connectionId: string; database: string }) => Promise<MysqlSchemaSnapshot>;
  readonly ensureOracleSchema: (opts: { connectionId: string; schema: string }) => Promise<OracleSchemaSnapshot>;
  readonly ensureSqliteSchema: (opts: { connectionId: string; database: string }) => Promise<SqliteSchemaSnapshot>;
  readonly ensureMssqlTables: (opts: { connectionId: string; database: string }) => Promise<MssqlTableSnapshot>;
  /**
   * Optional MetaInfo lookup: the user-declared JSON column declarations
   * (`DatabaseMetaInfo.jsonColumns`) for the given database. When omitted (or
   * returning `[]`), the projector runs without the MetaInfo branch — same
   * behavior as pre-A4. Errors are caught at the call site and treated as
   * no-declarations rather than blocking schema projection.
   */
  readonly fetchLooseJsonDeclarations?: (opts: { connectionId: string; database: string }) => Promise<readonly JsonColumnDeclaration[]>;
  /**
   * Optional MetaInfo lookup: the user-declared loose foreign keys
   * (`DatabaseMetaInfo.looseRelationships`) for the given database, including
   * JSON-path loose FKs (`sourcePath` set). When omitted (or returning `[]`),
   * the snapshot carries no `looseRelationships` — same behavior as pre-Phase-5.
   * Errors are caught at the call site and treated as no-relationships rather
   * than blocking schema projection. Mirrors `fetchLooseJsonDeclarations`.
   */
  readonly fetchLooseRelationships?: (opts: { connectionId: string; database: string }) => Promise<readonly LooseRelationship[]>;
};

export function buildPullSchemaSnapshot(
  connectionId: string,
  engine: SqlEngine,
  deps: PullSchemaDeps,
): PullSchemaSnapshotFn {
  return async (database) => {
    if (!database) return undefined;
    const looseDecls = await fetchLooseJsonDeclsSafe(deps, connectionId, database);
    const looseJsonIndex = buildLooseJsonHintIndex(looseDecls, engine);
    const looseRelationships = await fetchLooseRelationshipsSafe(deps, connectionId, database);
    let snapshot: SchemaSnapshot | undefined;
    switch (engine) {
      case 'postgres': snapshot = projectPgSchema(await deps.ensurePgSchema({ connectionId, database }), looseJsonIndex); break;
      case 'mysql':    snapshot = projectMysqlSchema(await deps.ensureMysqlSchema({ connectionId, database }), looseJsonIndex); break;
      case 'oracle':   snapshot = projectOracleSchema(await deps.ensureOracleSchema({ connectionId, schema: database }), looseJsonIndex); break;
      case 'sqlite':   snapshot = projectSqliteSchema(await deps.ensureSqliteSchema({ connectionId, database }), looseJsonIndex); break;
      case 'mssql':    snapshot = projectMssqlTables(await deps.ensureMssqlTables({ connectionId, database }), looseJsonIndex); break;
      default:         return undefined;
    }
    // Additive pass-through: attach the user-declared loose FKs onto the
    // projected snapshot's new field. Always present (possibly `[]`) so
    // consumers can rely on the array shape.
    return {
      ...snapshot,
      looseRelationships,
      revision: projectedSnapshotRevision(snapshot.fetchedAt, looseDecls, looseRelationships),
    };
  };
}

/**
 * `SchemaSnapshot.revision` for a pulled snapshot: the native cache's build
 * time plus a hash of the MetaInfo this pull folded in. A declaration write
 * re-pulls against an UNCHANGED native cache (same `fetchedAt`), so without the
 * hash the VQE suggestion caches would keep serving the pre-write join
 * candidates and JSON classifications. Content-derived rather than a counter so
 * two services of one connection that see the same content share a cache key.
 */
export function projectedSnapshotRevision(
  fetchedAt: number,
  looseDecls: readonly JsonColumnDeclaration[],
  looseRelationships: readonly LooseRelationship[],
): string {
  return `${fetchedAt}:${hashText(JSON.stringify([looseDecls, looseRelationships]))}`;
}

async function fetchLooseJsonDeclsSafe(
  deps: PullSchemaDeps,
  connectionId: string,
  database: string,
): Promise<readonly JsonColumnDeclaration[]> {
  if (!deps.fetchLooseJsonDeclarations) return [];
  try {
    return await deps.fetchLooseJsonDeclarations({ connectionId, database });
  } catch (err) {
    console.warn('[schemaCacheAdapter] failed to load loose-JSON declarations:', err);
    return [];
  }
}

async function fetchLooseRelationshipsSafe(
  deps: PullSchemaDeps,
  connectionId: string,
  database: string,
): Promise<readonly LooseRelationship[]> {
  if (!deps.fetchLooseRelationships) return [];
  try {
    return await deps.fetchLooseRelationships({ connectionId, database });
  } catch (err) {
    console.warn('[schemaCacheAdapter] failed to load loose relationships:', err);
    return [];
  }
}
